import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import type { DocChatFeedbackSubmission } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useSubmitDocChatFeedback'
import type { DocChatResponseCitations } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/buildDocChatEvalCase'
import { useMemo } from 'react'

export type DocChatFeedbackBase = Omit<
  DocChatFeedbackSubmission,
  'reasons' | 'comment' | 'selectedText'
>

export interface DocChatFeedbackTurn {
  conversationId?: string | null
  turnId?: string
  response?: DocChatResponseCitations
  copyText: string
  retryQuery: string
}

/**
 * What every feedback on one DocChat answer carries, and whether that answer
 * can be rated: it needs a saved conversation, a turn, a site and an answer.
 */
export function useDocChatFeedbackBase({
  conversationId,
  turnId,
  response,
}: Pick<DocChatFeedbackTurn, 'conversationId' | 'turnId' | 'response'>): {
  sessionId: string | null
  canGiveFeedback: boolean
  feedback: DocChatFeedbackBase
} {
  const { siteId } = useAdvancedSearchUX()
  const sessionId =
    conversationId?.trim() || response?.conversation_id?.trim() || null

  const feedback = useMemo<DocChatFeedbackBase>(
    () => ({ conversationId: sessionId ?? '', turnId: turnId ?? '', siteId }),
    [sessionId, siteId, turnId]
  )

  return {
    sessionId,
    canGiveFeedback: Boolean(sessionId && turnId && siteId && response),
    feedback,
  }
}
